import { useEffect, useState } from "react";
import { ApiError, accessApi, type AccessGroup } from "../../services/api";

/**
 * Administrators maintain groups here. A project's owner then grants one of
 * these groups a role; this page does not decide what a group can open.
 */
const ACTION_LABEL: Record<string, string> = {
  group_created: "Created a group",
  group_renamed: "Renamed a group",
  group_deleted: "Deleted a group",
  member_added: "Added a member",
  member_removed: "Removed a member",
  grant_set: "Set a role",
  grant_removed: "Removed a role",
  handed_over: "Handed something over",
};

export function GroupsPage() {
  const [groups, setGroups] = useState<AccessGroup[]>([]);
  const [accounts, setAccounts] = useState<Array<{ id: string; email: string }>>([]);
  const [audit, setAudit] = useState<Array<{ id: string; action: string; detail: Record<string, string>; createdAt: string }>>([]);
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  async function load() {
    const [listed, people, entries] = await Promise.all([
      accessApi.groups(),
      accessApi.users(),
      accessApi.audit(),
    ]);
    setGroups(listed);
    setAccounts(people);
    setAudit(entries);
  }

  useEffect(() => {
    load().catch((exc: unknown) => {
      setError(exc instanceof ApiError ? exc.message : "Groups could not be loaded.");
    });
  }, []);

  async function create() {
    setError("");
    try {
      await accessApi.createGroup(name.trim());
      setName("");
      await load();
    } catch (exc) {
      setError(exc instanceof ApiError ? exc.message : "The group was not created.");
    }
  }

  async function add(groupId: string, userId: string) {
    if (!userId) return;
    setError("");
    try {
      await accessApi.addMember(groupId, userId);
      await load();
    } catch (exc) {
      setError(exc instanceof ApiError ? exc.message : "That person was not added.");
    }
  }

  async function remove(groupId: string, userId: string) {
    setError("");
    try {
      await accessApi.removeMember(groupId, userId);
      await load();
    } catch (exc) {
      setError(exc instanceof ApiError ? exc.message : "That person was not removed.");
    }
  }

  async function removeGroup(groupId: string) {
    setError("");
    try {
      await accessApi.deleteGroup(groupId);
      await load();
    } catch (exc) {
      setError(exc instanceof ApiError ? exc.message : "The group was not deleted.");
    }
  }

  const emailOf = Object.fromEntries(accounts.map((account) => [account.id, account.email]));

  return (
    <div className="content-stack">
      <section className="hero compact-hero">
        <div>
          <h1>Groups</h1>
          <p>
            Add people to a group once. A project owner can then give that group
            a role, and everyone in it can open the project and its published model.
          </p>
        </div>
      </section>
      {error && <p className="inline-notice warning" role="alert">{error}</p>}
      <section className="panel">
        <div className="panel-heading">
          <div>
            <h2>New group</h2>
            <p>Name the team. You can add people after it exists.</p>
          </div>
        </div>
        <form
          className="groups-create"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <label>
            Group name
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Service Operation"
            />
          </label>
          <button type="submit" className="button primary" disabled={!name.trim()}>
            Create group
          </button>
        </form>
      </section>
      {groups.map((group) => {
        const available = accounts.filter((account) => !group.memberIds.includes(account.id));
        return (
          <section className="panel" key={group.id}>
            <div className="panel-heading">
              <div>
                <h2>{group.name}</h2>
                <p>
                  {group.memberIds.length === 0
                    ? "No one in this group yet."
                    : `${group.memberIds.length} ${group.memberIds.length === 1 ? "person" : "people"}`}
                </p>
              </div>
              <button type="button" className="button tertiary groups-delete" onClick={() => removeGroup(group.id)}>
                Delete group
              </button>
            </div>
            <ul className="groups-members">
              {group.memberIds.map((userId) => {
                const email = emailOf[userId] || userId;
                return (
                  <li key={userId}>
                    <span className="groups-avatar" aria-hidden="true">{email.slice(0, 1).toUpperCase()}</span>
                    <span className="groups-email">{email}</span>
                    <button type="button" className="button tertiary" onClick={() => remove(group.id, userId)}>
                      Remove
                    </button>
                  </li>
                );
              })}
            </ul>
            <label className="groups-add">
              Add a person
              <select
                aria-label={`Add someone to ${group.name}`}
                defaultValue=""
                disabled={available.length === 0}
                onChange={(event) => {
                  void add(group.id, event.target.value);
                  event.target.value = "";
                }}
              >
                <option value="">{available.length === 0 ? "Everyone is already in this group" : "Choose an account…"}</option>
                {available.map((account) => (
                  <option key={account.id} value={account.id}>{account.email}</option>
                ))}
              </select>
            </label>
          </section>
        );
      })}
      <section className="panel">
        <div className="panel-heading">
          <div>
            <h2>Recent changes</h2>
            <p>Who was added, removed, or given a role.</p>
          </div>
        </div>
        <ul className="groups-audit">
          {audit.length === 0 && <li className="groups-audit-empty">No access changes yet.</li>}
          {audit.map((entry) => (
            <li key={entry.id}>
              <span>
                {ACTION_LABEL[entry.action] || entry.action}
                {entry.detail.name ? ` — ${entry.detail.name}` : ""}
                {entry.detail.role ? ` (${entry.detail.role})` : ""}
              </span>
              <time dateTime={entry.createdAt}>{new Date(entry.createdAt).toLocaleString()}</time>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
